import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { tyreSurveyAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CircleDot, Truck, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const TyreSurveySetupBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const envPrefix = location.pathname.startsWith('/test') ? '/test' : '';
  const { isSupervisor, isAdmin } = useAuth();
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minimized, setMinimized] = useState(false);

  const fetchSetupStatus = useCallback(async () => {
    if (!isSupervisor && !isAdmin) return;
    
    try {
      const response = await tyreSurveyAPI.getTrucksWithoutSetup();
      setSetupStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch tyre survey setup status:', error);
    } finally {
      setLoading(false);
    }
  }, [isSupervisor, isAdmin]);

  useEffect(() => {
    fetchSetupStatus();
    // Refresh status every 5 minutes
    const interval = setInterval(fetchSetupStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSetupStatus]);

  // Don't show for non-supervisors or if all trucks have setups
  if (!isSupervisor && !isAdmin) return null;
  if (loading) return null;
  if (!setupStatus || setupStatus.setup_required_count === 0) return null;

  const pendingCount = setupStatus.setup_required_count;
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-20 z-50 bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-orange-600 transition-colors flex items-center gap-2"
        onClick={() => setMinimized(false)}
        data-testid="tyre-setup-banner-minimized"
      >
        <CircleDot className="w-5 h-5" />
        <span className="font-semibold">{pendingCount} tyre setups pending</span>
      </div>
    );
  }

  return (
    <div 
      className="bg-orange-50 border-b-2 border-orange-400 px-4 py-3"
      data-testid="tyre-setup-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-orange-500 p-2 rounded-lg">
            <CircleDot className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-orange-800">
              Monthly Tyre Survey Setup Required
            </p>
            <p className="text-sm text-orange-700">
              {pendingCount} of {setupStatus.total_trucks} trucks require tyre survey setup for {currentMonth}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button 
            onClick={() => navigate(`${envPrefix}/tyre-survey`)}
            className="bg-orange-600 hover:bg-orange-700 text-white flex-1 sm:flex-none"
            data-testid="tyre-setup-btn"
          >
            Configure Setups
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setMinimized(true)}
            className="text-orange-700 hover:bg-orange-100"
            title="Minimize (will appear at bottom right)"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Show trucks without setup */}
      {setupStatus.trucks_without_setup?.length > 0 && (
        <div className="max-w-7xl mx-auto mt-2 flex flex-wrap gap-2">
          <span className="text-xs text-orange-700 font-medium">Pending:</span>
          {setupStatus.trucks_without_setup.slice(0, 5).map(t => (
            <span key={t.id} className="text-xs bg-orange-200 px-2 py-0.5 rounded font-mono flex items-center gap-1">
              <Truck className="w-3 h-3" />
              {t.registration_number}
            </span>
          ))}
          {setupStatus.trucks_without_setup.length > 5 && (
            <span className="text-xs text-orange-600">
              +{setupStatus.trucks_without_setup.length - 5} more
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TyreSurveySetupBanner;
